import { Intent } from "@blueprintjs/core";
import { CommentableEntity, DELETE_COMMENT } from "@hex/common";
import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import {
  HexButton,
  HexMenu,
  HexMenuItem,
  HexPopover,
} from "../../hex-components";
import { useCommentGetter } from "../../hex-version-multiplayer/state-hooks/commentStateHooks";
import { ORG_ID } from "../../orgs";
import { Routes } from "../../route/routes";
import { useHexVersionAOContext } from "../../util/hexVersionAOContext";
import { useProjectContext } from "../../util/projectContext";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { useToaster } from "../common/Toasts";
import {
  EditIcon,
  LinkIcon,
  MoreMenuIcon,
  TrashIcon,
} from "../icons/CustomIcons";

type CommentLinkType = "app" | "project" | "component";

const StyledMenu = styled(HexMenu)`
  min-width: 90px;
`;

export interface CommentActionsProps {
  isAppCommentInProject?: boolean;
  className?: string;
  commentId: string;
  setEditing: () => void;
  commentEditable?: boolean;
  onMenuOpened?: () => void;
  onMenuClosed?: () => void;
}

export const CommentActions: React.ComponentType<CommentActionsProps> =
  React.memo(function CommentActions({
    className,
    commentEditable = false,
    commentId,
    isAppCommentInProject = false,
    onMenuClosed,
    onMenuOpened,
    setEditing,
  }) {
    const { hexId, isComponent, version } = useProjectContext();

    const { dispatchAO } = useHexVersionAOContext();
    const toaster = useToaster();
    const getComment = useCommentGetter();

    const [isConfirmDeleteDialogOpen, setCommentToDelete] =
      useState<boolean>(false);

    const toggleDeleteCommentDialog = useCallback(async () => {
      setCommentToDelete((prev) => !prev);
    }, [setCommentToDelete]);

    const deleteComment = useCallback(async () => {
      dispatchAO(DELETE_COMMENT.create(commentId));
    }, [commentId, dispatchAO]);

    const copyLink = useCallback(
      async (type: CommentLinkType) => {
        let comment = getComment(commentId);

        // comments can be the children of other comments, but only
        // one level deep, so we use the parent comment to find the cell and id to link to
        if (comment.entityType === CommentableEntity.COMMENT) {
          comment = getComment(comment.entityId);
        }

        const route =
          type === "app"
            ? Routes.APP.getUrl({
                hexId,
                version,
                urlParams: {
                  selectedStaticCellId: comment.entityId,
                },
              })
            : type === "component"
            ? Routes.COMPONENT.getUrl({
                hexId,
                version,
                urlParams: {
                  selectedStaticCellId: comment.entityId,
                  focusedCommentId: comment.id,
                },
              })
            : Routes.LOGIC.getUrl({
                hexId,
                version,
                urlParams: {
                  selectedStaticCellId: comment.entityId,
                  focusedCommentId: comment.id,
                },
              });

        await navigator.clipboard.writeText(
          `${window.location.origin}/${ORG_ID}${route}`,
        );
        toaster.show({
          message: (
            <>
              <strong>Comment link</strong> copied to clipboard!
            </>
          ),
        });
      },
      [commentId, getComment, hexId, toaster, version],
    );

    const actionMenu = useMemo(
      () => (
        <StyledMenu>
          {!isAppCommentInProject ? (
            <HexMenuItem
              icon={<LinkIcon />}
              text="Copy link"
              // eslint-disable-next-line react/jsx-no-bind
              onClick={() => copyLink(!isComponent ? "project" : "component")}
            />
          ) : (
            <HexMenuItem icon={<LinkIcon />} text="Copy link">
              <HexMenuItem
                text="Copy link in project"
                // eslint-disable-next-line react/jsx-no-bind
                onClick={() => copyLink("project")}
              />
              <HexMenuItem
                text="Copy link in app"
                // eslint-disable-next-line react/jsx-no-bind
                onClick={() => copyLink("app")}
              />
            </HexMenuItem>
          )}
          {commentEditable && (
            <>
              <HexMenuItem
                icon={<EditIcon />}
                text="Edit"
                onClick={setEditing}
              />
              <HexMenuItem
                icon={<TrashIcon />}
                intent={Intent.DANGER}
                text="Delete"
                onClick={toggleDeleteCommentDialog}
              />
            </>
          )}
        </StyledMenu>
      ),
      [
        isAppCommentInProject,
        commentEditable,
        setEditing,
        toggleDeleteCommentDialog,
        copyLink,
        isComponent,
      ],
    );

    const popoverChildren = useMemo(
      () => (
        <HexButton
          $extraSmall={true}
          className={className}
          icon={<MoreMenuIcon />}
          minimal={true}
        />
      ),
      [className],
    );

    return (
      <>
        <ConfirmationDialog
          confirmationButtonIntent="danger"
          confirmationButtonText="Delete comment"
          confirmationText="Are you sure you want to delete this comment?"
          isOpen={isConfirmDeleteDialogOpen}
          title="Confirm delete"
          onClose={toggleDeleteCommentDialog}
          onConfirm={deleteComment}
        />
        <HexPopover
          captureDismiss={true}
          content={actionMenu}
          minimal={true}
          placement="bottom-end"
          onClosed={onMenuClosed}
          onOpened={onMenuOpened}
        >
          {popoverChildren}
        </HexPopover>
      </>
    );
  });
